import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {UntypedFormBuilder} from '@angular/forms';
import {
    AccountCreate,
    AccountUpload,
    AmountBounds,
    BillingType,
    BusinessDetails,
    MessageCounters,
    UploadError,
    UploadWarning,
    WebAccountBatchService,
    WebAccountService,
    WebBusinessService,
    WebUploadService
} from 'web-angular-client';
import {interval, Observable} from 'rxjs';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {ConfirmationService} from '../../../sdk/service/confirmation.service';
import {ASecureForm} from '../../../sdk/interface/form/ASecureForm';
import {AlertService} from '../../../sdk/service/alert.service';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import * as FileSaver from 'file-saver';
import moment from 'moment/moment';
import {LayoutService} from '../../layout/layout.service';
import {SubscriptionService} from '../../../sdk/service/subscription.service';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {PaymentRequestUploadUtils} from './payment-request-upload.utils';

@Component({
    selector: 'app-payment-request-upload',
    templateUrl: './payment-request-upload.component.html',
    styleUrls: ['./payment-request-upload.component.scss'],
    standalone: false
})
export class PaymentRequestUploadComponent extends ASecureForm implements OnInit, AfterViewInit
{
    /** The list of errors received when attempting to parse the upload CSV file. */
    uploadErrorList: Array<UploadError>;
    /** The list of warnings received when attempting to parse the upload CSV file. */
    uploadWarningList: Array<UploadWarning>;
    /** The list of accountCreate objects successfully parsed and created from the upload CSV file. */
    accountCreateList: Array<AccountCreate>;
    /** Represents the upload file once selected */
    paymentFile: File;
    /** used for displaying the Custom/Account reference */
    useCustomAccountNumbers = false;
    /** used for displaying the amount inclusive or exclusive of VAT */
    useVatInclusiveAmount = false;
    /** Represents the name of the batch to be created during upload*/
    batchName = '';
    /** Represents the billing type for the bulk upload*/
    generateInvoice = false;
    /** Represents the minimum Request To Pay Transaction amount*/
    minimumRequestToPayAmount: number;
    /** Represents the allow Partial Payments state for the bulk upload*/
    allowPartialPayments: Boolean = false;
    /** Represents the email notification for the bulk upload*/
    sendEmail = false;
    /** Represents the sms notification type for the bulk upload*/
    sendSMS = false;
    /** Represents the sms notification if SMS portal has insufficient credits to perform the bulk account*/
    showSmsUnavailable: boolean = false;
    messageCounters: MessageCounters = {emailCounter: 0, smsCounter: 0};
    isMultiPremiumEnabled = false;
    /** Used to track and manipulate the contents of the file input selector used by users to
     *  select their desired upload file */
    @ViewChild('fileInput') protected fileInput: ElementRef;
    @ViewChild('sendButton') protected buttonSendElement: ElementRef;
    @ViewChild('clearButton') protected buttonClearElement: ElementRef;
    @ViewChild('batchNameField') protected batchNameField: ElementRef;
    /** Used to turn the loading progress bar symbol on/off */
    protected loading = false;
    private shouldFocusSendButton: boolean;
    private shouldFocusClearButton: boolean;
    private isBatchNameEdited: boolean;
    private accountUpload: AccountUpload;
    private maxFileSize = 10000000; // 10mb

    constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected businessService: WebBusinessService,
        protected fileUploadService: WebUploadService,
        protected confirmationService: ConfirmationService,
        protected environmentService: EnvironmentService,
        private ngxLoader: NgxUiLoaderService,
        private batchAccountService: WebAccountBatchService,
        private layoutService: LayoutService,
        private subscriptionService: SubscriptionService,
        private accountService: WebAccountService)
    {
        super(authenticationService, environmentService);
    }

    get accountPreviewList(): Array<AccountCreate>
    {
        return this.accountCreateList?.slice(0, 10) ?? [];
    }

    get accountWarningPreviewList(): Array<UploadWarning>
    {
        return this.uploadWarningList?.slice(0, 10) ?? [];
    }

    get accountErrorPreviewList(): Array<UploadError>
    {
        return this.uploadErrorList?.slice(0, 10) ?? [];
    }

    get isSendDisabled(): boolean
    {
        return this.isLoading() ||
            !this.isNonEmptyAccountPreview() ||
            this.hasUploadErrors() ||
            (!!this.sendEmail && this.hasInsufficientEmailCredits()) ||
            (!!this.sendSMS && this.hasInsufficientMerchantSmsCredits()) ||
            (!!this.sendSMS && this.showSmsUnavailable) ||
            !this.isSubscriptionValid();
    }

    ngOnInit(): void
    {
        this.uploadErrorList = [];
        this.accountCreateList = [];
        this.uploadWarningList = [];
        this.loadMessageCounters();
        this.layoutService.loadPrepaidServicesStatusInfo();
        this.loadMinimumRequestToPayAmount();

        setInterval(() =>
        {
            this.loadMessageCounters();
        }, 10000);

        this.businessService.getBusinessDetails().subscribe({
                next: (businessDetails: BusinessDetails) =>
                {
                    this.useCustomAccountNumbers = businessDetails.customRefEnabled;
                    this.useVatInclusiveAmount = businessDetails.useVatInclusiveAmount;
                    this.generateInvoice = businessDetails.sendInvoice;
                    this.isMultiPremiumEnabled = businessDetails.multiPremiumEnabled;

                    this.updateSendEmail();
                },
                error: () => this.alertService.error('Failed to load business settings. Please try again.')
            }
        );
    }

    ngAfterViewInit(): void
    {
        this.clearAll();
        // this function will determine which element on the page should have focus. A timer is used to keep
        // focus on the desired element despite user input events (e.g. clicks) which normally cause focus to be lost.
        interval(10000).subscribe(() =>
        {
            if (this.shouldFocusSendButton && this.isBatchNameEdited)
            {
                this.focusOnElement(this.buttonSendElement);
            }
            else if (this.shouldFocusSendButton && !this.isBatchNameEdited)
            {
                this.focusOnElement(this.batchNameField);
            }
            else if (this.shouldFocusClearButton)
            {
                this.focusOnElement(this.buttonClearElement);
            }
            else
            {
                this.focusOnElement(this.fileInput);
            }
        });
    }

    loadMinimumRequestToPayAmount()
    {
        this.accountService.getMinMaxRequestToPayAmountBounds().subscribe({
            next: (amountBoundsDTO: AmountBounds) =>
            {
                this.minimumRequestToPayAmount = amountBoundsDTO.minimumAmount;
            }, error: () =>
            {
                this.alertService.error('An error occurred while retrieving amount bounds');
            }
        });
    }

    public updateSendEmail(): void
    {
        this.sendEmail = this.generateInvoice ? true : this.sendEmail;
    }

    public updateGenerateInvoice(): void
    {
        this.generateInvoice = this.sendEmail ? this.generateInvoice : this.sendEmail;
    }

    public uploadAccountCsvFile = (paymentFile: File): Observable<any> =>
    {
        return this.fileUploadService.uploadAccountBulkCsvFile(paymentFile);
    };

    /**
     * Handles a file input event. Sets the file selected by the user and performs some basic
     * validation on the file type.
     */
    public previewAndValidateFile(event): boolean
    {
        if (this.isGuestAdminUser())
        {
            this.alertService.error('Guest accounts cannot upload bulk payment files.', false);
            return;
        }

        this.clearLists();
        if (event.target.files[0])
        {
            const file = event.target.files[0];
            this.debugOutput(`file size is ${file.size} and file type is ${file.type}`);
            this.paymentFile = event.target.files.item(0);
            if (!this.isExpectedFileType(this.paymentFile))
            {
                this.alertService.error('Incorrect file type received. Only .csv files are supported');
                this.paymentFile = null;
                return false;
            }
            if (!this.isExpectedFileSize(file))
            {
                this.alertService.error('File is too large. The maximum supported file size is ' + this.maxFileSize / 1000000 + ' MB.');
                this.paymentFile = null;
                return false;
            }
            this.loading = true;
            this.ngxLoader.start();
            this.uploadAccountCsvFile(this.paymentFile).subscribe({
                next: (accountUpload: AccountUpload) =>
                {
                    this.uploadErrorList = new Array<UploadError>();
                    this.uploadWarningList = new Array<UploadWarning>();
                    this.accountCreateList = new Array<AccountCreate>();
                    this.ngxLoader.stop();
                    this.loading = false;
                    this.accountUpload = accountUpload;
                    this.accountCreateList = accountUpload.accountCreate;
                    this.uploadErrorList = accountUpload.uploadError;
                    this.uploadWarningList = accountUpload.uploadWarning;
                    this.hasSufficientSystemSmsBalance(this.accountCreateList.length);
                    // set the focus to the clear button if there are errors otherwise focus on the send button
                    if (this.hasUploadErrors())
                    {
                        this.alertService.error('The current upload file contains errors. Please correct the errors' + ' in the source file and resubmit.');
                        this.focusClearButton();
                    }
                    else
                    {
                        this.focusSendButton();
                    }
                }, error: (error) =>
                {
                    this.loading = false;
                    this.ngxLoader.stop();
                    this.debugOutput(error);
                    this.alertService.error(error);
                }
            });
        }
    }

    public focusBatchName(): void
    {
        this.isBatchNameEdited = !!this.batchName;
    }

    public isNonEmptyAccountPreview(): boolean
    {
        return (this.accountCreateList != null && this.accountCreateList?.length > 0) ||
            (this.uploadWarningList != null && this.uploadWarningList.length > 0);
    }

    public hasUploadErrors(): boolean
    {
        return (this.uploadErrorList != null && this.uploadErrorList.length > 0);
    }

    public hasUploadWarnings(): boolean
    {
        return (this.uploadWarningList != null && this.uploadWarningList.length > 0);
    }

    public hasInsufficientEmailCredits(): boolean
    {
        return !!this.messageCounters && this.messageCounters?.emailCounter < this.accountCreateList?.length;
    }

    public hasInsufficientMerchantSmsCredits(): boolean
    {
        return !!this.messageCounters && this.messageCounters?.smsCounter < this.accountCreateList?.length;
    }

    public hasInsufficientSystemSmsCredits(): boolean
    {
        return this.showSmsUnavailable;
    }

    public isAccountListPopulated(): boolean
    {
        return (this.isNonEmptyAccountPreview() ||
            this.hasUploadErrors() ||
            this.hasUploadWarnings());
    }

    public getFileName(): string
    {
        if (!this.paymentFile)
        {
            return 'Browse for payment file...';
        }
        return this.paymentFile.name;
    }

    /**
     * Angular or the web browser can incorrectly identify csv files as
     * 'application/vnd.ms-excel' or
     * 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'.
     */
    public isExpectedFileType(file: File): boolean
    {
        return (file.name != null && file.type === 'application/vnd.ms-excel' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'text/csv');
    }

    public clearAll(): void
    {
        this.paymentFile = null;
        this.clearFileInput();
        this.clearLists();
        this.clearFocusElements();
        this.sendSMS = false;
        this.sendEmail = false;
        this.generateInvoice = false;
    }

    public clearUploadHistory(): void
    {
    }

    public clearFileInput(): void
    {
        this.fileInput.nativeElement.value = '';
    }

    public getFileFormatDescription(): string
    {
        return PaymentRequestUploadUtils.getUploadFileFormatDescription(
            this.useVatInclusiveAmount,
            this.useCustomAccountNumbers,
            this.isMultiPremiumEnabled);
    }

    downloadSampleUploadFile(): void
    {
        const {useCustomAccountNumbers, isMultiPremiumEnabled} = this;

        const blob = PaymentRequestUploadUtils.generateSampleFileContentCsvBlob(
            useCustomAccountNumbers,
            isMultiPremiumEnabled);
        const fileName = PaymentRequestUploadUtils.getSampleFileName(
            useCustomAccountNumbers,
            isMultiPremiumEnabled
        );

        FileSaver.saveAs(blob, fileName);
    }

    public getUploadConfirmation(): string
    {
        return `Are you sure you wish to create a batch ${this.batchName ? 'called: ' + this.batchName : ''},
        and ${!(this.sendSMS || this.sendEmail) ? 'not send any email/sms notification? ' : `send ${this.accountCreateList.length} request to pay transactions?`}`;
    }

    public downloadValidationFile(): void
    {
        this.ngxLoader.start();
        this.fileUploadService.getBulkValidationFile(this.accountUpload).subscribe({
            next: (blob: Blob) =>
            {
                FileSaver.saveAs(blob, `Bulk Validation File ${moment(new Date()).format('YYYY-MM-DD HH:mm:ss')}`);
                this.ngxLoader.stop();
            }, error: error =>
            {
                this.debugOutput(error);
                this.alertService.error('Failed to retrieve bulk validation file. Please try again later.');
                this.ngxLoader.stop();
            }
        });
    }

    /**
     * Submits the current account upload file for bulk account creation. Successful and failed
     * account uploads will be displayed and email and/or SMS notifications will be sent out to all
     * successfully uploaded account.
     */
    submit(): void
    {
        this.updateAccountsForSubmission();
        this.confirmationService.confirm(
            'Send Request to Pay Transaction',
            this.getUploadConfirmation())
            .then((confirmed) =>
            {
                if (confirmed)
                {
                    // user clicked ok/save - handle the confirmation event
                    this.loading = true;
                    this.ngxLoader.start();

                    this.batchAccountService.bulkCreateAccounts(
                        this.sendEmail,
                        this.sendSMS,
                        this.accountCreateList,
                        this.batchName).subscribe({
                        next: () =>
                        {
                            this.loading = false;
                            this.ngxLoader.stop();
                            this.clearAll();
                            this.alertService.success(
                                'Request to pay uploaded for processing successful. Please view batch status on dashboard');
                        },
                        error: (error) =>
                        {
                            this.loading = false;
                            this.ngxLoader.stop();
                            this.clearAll();
                            this.debugOutput(error);
                            this.alertService.error(error);
                        }
                    });
                }
            })
            .catch(() =>
            {
                // user closed the dialogue box or an error occurred while trying to display the
                // dialogue box
            });
    }

    public isLoading(): boolean
    {
        return this.loading;
    }

    public isWindowWidthSmall(): boolean
    {
        return window.matchMedia('(max-width: 767px)').matches;
    }

    public isSubscriptionValid(): boolean
    {
        return this.subscriptionService.isSubscriptionValid;
    }

    isAllowPartialPayments()
    {
        return this.allowPartialPayments ? 'YES' : 'NO';
    }

    getDifferenceInDays(dateTimeExpire: string | undefined)
    {
        const timeDifferenceInMs = new Date(dateTimeExpire).getTime() - new Date().getTime();
        const aDayInMs = 24 * 60 * 60 * 1000;

        return Math.floor(timeDifferenceInMs / aDayInMs);
    }

    hasSufficientSystemSmsBalance(bulkRtpSize: number)
    {
        this.batchAccountService.hasSufficientSystemSmsBalance(bulkRtpSize).subscribe({
            next: (hasSufficientSms: boolean) =>
            {
                this.showSmsUnavailable = !hasSufficientSms;
            }, error: () =>
            {
                this.alertService.error(`Failed to retrieve information from our SMS service provider. Please try again later.`);
            }
        });
    }

    protected focusOnElement(domElement: ElementRef): void
    {
        setTimeout(() =>
        {
            domElement.nativeElement.focus();
        }, 10);
    }

    protected focusSendButton(): void
    {
        this.shouldFocusSendButton = true;
        this.shouldFocusClearButton = false;
    }

    protected focusClearButton(): void
    {
        this.shouldFocusSendButton = false;
        this.shouldFocusClearButton = true;
    }

    protected isExpectedFileSize(file: File): boolean
    {
        return (file.size <= this.maxFileSize);
    }

    protected clearFocusElements(): void
    {
        this.buttonSendElement.nativeElement.blur();
        this.buttonClearElement.nativeElement.blur();
        this.shouldFocusClearButton = false;
        this.shouldFocusSendButton = false;
        this.isBatchNameEdited = false;
    }

    protected clearPreviewLists(): void
    {
        this.uploadErrorList = null;
        this.accountCreateList = null;
        this.uploadWarningList = null;
    }

    protected clearLists(): void
    {
        this.clearPreviewLists();
        this.clearUploadHistory();
    }

    private updateAccountsForSubmission(): void
    {
        this.accountCreateList.forEach(accountCreate =>
        {
            accountCreate.billingType = this.generateInvoice ? BillingType.INVOICE : BillingType.STATEMENT;

            if (this.allowPartialPayments)
            {
                accountCreate.minimumAmount = this.minimumRequestToPayAmount;
                accountCreate.maximumAmount = accountCreate.amount;
            }
        });
    }

    private loadMessageCounters()
    {
        if (this.authenticationService.isLoggedIn())
        {
            this.businessService.getCounters()
                .subscribe((messageCountersDTO) =>
                {
                    this.messageCounters = messageCountersDTO;
                });
        }
    }
}